import { Action, Selector, State, StateContext } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import { ExcelService } from '../../services/excel-upload.service';
import { Observable, Subject, switchMap, timer, takeUntil, tap, map, filter, catchError, of, EMPTY } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { UploadExcelForWorkflow } from './excel-upload.actions';
import { SignedURLDto, UploadDto } from '@monorepo/backend-lib/dtos';
import { UploadStatus } from '@monorepo/interfaces';

export interface ExcelUploadStateModel {
    isUploading: boolean;
    currentFile: File | null;
    currentS3FileId: string | null;
}

@State<ExcelUploadStateModel>({
    name: 'excelUpload',
    defaults: {
        isUploading: false,
        currentFile: null,
        currentS3FileId: null
    }
})
@Injectable()
export class ExcelUploadState {
    private excelService = inject(ExcelService);
    private pollingStop$ = new Subject<void>();

    @Selector()
    static isUploading(state: ExcelUploadStateModel): boolean {
        return state.isUploading;
    }

    @Selector()
    static currentS3FileId(state: ExcelUploadStateModel): string | null {
        return state.currentS3FileId;
    }

    @Action(UploadExcelForWorkflow)
    uploadExcelForWorkflow(
        ctx: StateContext<ExcelUploadStateModel>,
        action: UploadExcelForWorkflow
    ): Observable<void | HttpErrorResponse> {
        ctx.patchState({
            isUploading: true,
            currentFile: action.excelFile
        });

        return this.getPreSignedUploadLink(ctx, action.workflowId);
    }

    private getPreSignedUploadLink(
        ctx: StateContext<ExcelUploadStateModel>,
        workflowId: string
    ): Observable<void | HttpErrorResponse> {
        return this.excelService.getPreSignedUploadLink(workflowId).pipe(
            switchMap((signedUrlDto: SignedURLDto) => {
                const state = ctx.getState();
                if (state.currentFile) {
                    return this.uploadFileToS3(ctx, signedUrlDto.signedURL, state.currentFile, workflowId);
                }
                return EMPTY;
            }),
            catchError((error: HttpErrorResponse) => {
                ctx.patchState({ isUploading: false });
                return of(error);
            })
        );
    }

    private uploadFileToS3(
        ctx: StateContext<ExcelUploadStateModel>,
        uploadUrl: string,
        file: File,
        dataId: string
    ): Observable<void | HttpErrorResponse> {
        return this.excelService.uploadFileToS3(uploadUrl, file).pipe(
            switchMap(() => this.startPollingUploadStatus(ctx, dataId)),
            catchError((error: HttpErrorResponse) => {
                ctx.patchState({ isUploading: false });
                return of(error);
            })
        );
    }

    private startPollingUploadStatus(ctx: StateContext<ExcelUploadStateModel>, dataId: string): Observable<void> {
        this.pollingStop$.next(); // Reset any previous polling
        return timer(0, 2000).pipe(
            takeUntil(this.pollingStop$),
            switchMap(() => this.excelService.getUserUploads()),
            catchError(() => {
                ctx.patchState({ isUploading: false });
                return of([]);
            }),
            map(
                (uploads: UploadDto[]) =>
                    uploads
                        .filter(upload => upload.dataId === dataId)
                        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]
            ),
            filter((upload): upload is UploadDto => !!upload && upload.status === UploadStatus.DATA_PARSED),
            tap(upload => this.stopPollingUploadStatus(ctx, upload.id)),
            switchMap(() => EMPTY)
        );
    }

    private stopPollingUploadStatus(ctx: StateContext<ExcelUploadStateModel>, currentS3FileId: string | null): void {
        this.pollingStop$.next();
        ctx.patchState({ isUploading: false, currentS3FileId });
    }
}
